import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  CHEF_FILTER_DATA,
  RESTAURANT_CHEF_CREATE,
  RESTAURANT_CHEF_VIEW,
} from '../../../../../constants/restaurantConst';
import {
  RBAC_PERMISSION_LEVEL_FULL_ACCESS,
  RBAC_TYPE_HOTEL_RESTAURANT,
} from '../../../../../constants/RoleConsts';
import LinearProgressBar from '../../../../../shared/progress-bar/Progress';
import {
  allRestaurantChefSelector,
  allRestaurantSelector,
  fetchAllRestaurant,
  setAllRestaurantChef,
} from '../../../../property/propertySlice';
import SearchBar from '../../../../../shared/search-bar/Search-Bar';
import ChefListTable from './Chef-List-Table';
import DeletePopupModel from '../../../../../shared/delete-popup/Delete-Popup';
import { createDataSortMapping, deleteChefDetails } from '../../../../../utils/PropertyAPIUtils';
import { notificationManager } from '../../../../../utils/NotificationUtils';
import useOutsideAlerter from '../../../../../custom-hooks/useOutsideAlerter';
import { decryptLocalStorage } from '../../../../../utils/authUtils';

const ChefList = (props) => {
  const {
    onDrawerToggle,
    onViewToggle,
    allRestaurantLoader,
    hotelId,
    permissions,
    page,
    rowsPerPage,
    onsetRowsPerPage,
    onsetPage,
  } = props;
  const dispatch = useDispatch();
  const allRestaurantChefList = useSelector(allRestaurantChefSelector);
  const restaurantList = useSelector(allRestaurantSelector);
  const [chefList, setChefList] = useState([]);
  const [filteredBaseList, setFilteredBaseList] = useState([]);
  const [selectedValue, setSelectedValue] = useState({
    id: '',
    name: '',
    title: '',
    description: '',
    status: false,
    anchorEl: null,
  });
  const [chefFilter, setChefFilter] = useState([]);
  const [visible, setVisible] = useState(false);
  const wrapperRef = useRef(null);
  const prevHotelIdRef = useRef(hotelId);
  const [popUp, setPopUp] = useState(false);
  const [tempSearchText, setTempSearchText] = useState('');
  const [dragEnabled, setDragEnabled] = useState(true);
  const dragDisabled = !dragEnabled || chefList?.length <= 1;

  const resetFilterDataList = () => {
    if (restaurantList && restaurantList?.length > 0) {
      const restaurantListData = restaurantList.map((value) => {
        return { id: value.id, name: value.name, selected: false };
      });
      CHEF_FILTER_DATA[1].subMenu = restaurantListData;
      setChefFilter(CHEF_FILTER_DATA);
    }
  };

  useEffect(() => {
    resetFilterDataList();
  }, [restaurantList]);

  useEffect(() => {
    setTempSearchText('');
    setFilteredBaseList(allRestaurantChefList);
    setChefList(allRestaurantChefList);
  }, [allRestaurantChefList]);

  useEffect(() => {
    if (allRestaurantLoader || chefList?.length === 0) return;
    const totalItems = chefList?.length;
    const maxPageIndex = Math.max(Math.ceil(totalItems / rowsPerPage) - 1, 0);
    if (page > maxPageIndex) {
      onsetPage(maxPageIndex);
    }
  }, [chefList?.length, rowsPerPage, page, onsetPage]);

  useEffect(() => {
    if (prevHotelIdRef?.current !== hotelId) {
      onsetPage(0);
    }
    prevHotelIdRef.current = hotelId;
  }, [hotelId]);

  const setSearchText = async (searchText) => {
    setTempSearchText(searchText);
    const filteredRows = filteredBaseList.filter((item) =>
      item.name?.toLowerCase().includes(searchText.toLowerCase()),
    );
    onsetPage(0);
    setChefList(filteredRows);
  };

  const handleCellMenuClick = (event, chef) => {
    setSelectedValue({
      id: chef.id,
      name: '',
      title: '',
      description: '',
      status: false,
      anchorEl: event.currentTarget,
      chef,
    });
  };

  const handleClose = () => {
    setSelectedValue({
      id: '',
      name: '',
      title: '',
      description: '',
      status: false,
      anchorEl: null,
      chef: null,
    });
  };

  const onOpenDrawer = (event, type) => {
    event.stopPropagation();
    onDrawerToggle(type, true, selectedValue.chef);
    handleClose();
  };

  const onViewDetails = (chef, e) => {
    if (e?.target?.tagName === 'TD') {
      onViewToggle(RESTAURANT_CHEF_VIEW, true, chef);
    }
  };

  const onClickView = (e) => {
    if (selectedValue && selectedValue.chef) {
      onViewToggle(RESTAURANT_CHEF_VIEW, true, selectedValue.chef);
    }
  };

  const handleChangePage = (event, newPage) => {
    onsetPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    onsetRowsPerPage(parseInt(event.target.value, 10));
    onsetPage(0);
  };

  const handleDeletePopupOpen = (event, chef) => {
    event.preventDefault();
    setSelectedValue((prev) => ({
      ...prev,
      title: 'Are you sure you want to delete',
      name: selectedValue.chef.name,
      description: 'This action cannot be reversed',
      status: true,
      anchorEl: null,
    }));
  };

  const onDelete = async () => {
    try {
      if (selectedValue && selectedValue?.id) {
        const lang = decryptLocalStorage('lang');
        const language = lang !== 'en' ? lang : '';
        await deleteChefDetails(hotelId, selectedValue.id, language);
        dispatch(fetchAllRestaurant(hotelId));
        notificationManager('success', 'Chef deleted successfully');
      }
      handleClose();
    } catch (e) {
      notificationManager(
        'danger',
        e.graphQLErrors ? e.graphQLErrors[0].message : 'Unable to delete Chef',
      );
      handleClose();
    }
  };

  const onDeleteCancel = () => {
    handleClose();
  };

  const filteredRecoard = (filters, field) => {
    if (field === 'all') {
      setFilteredBaseList(allRestaurantChefList);
      setChefList(allRestaurantChefList);
      resetFilterDataList();
    } else {
      if (filters && filters.length > 0) {
        const filteredRows = allRestaurantChefList.filter((chefData) => {
          return filters.some(
            (fItem) => fItem.id.toLowerCase() === chefData[field].toLowerCase() && fItem.selected,
          );
        });
        onsetPage(0);
        setFilteredBaseList(filteredRows);
        setChefList(filteredRows);
      } else {
        setFilteredBaseList(allRestaurantChefList);
        setChefList(allRestaurantChefList);
      }
    }
  };

  useOutsideAlerter(wrapperRef, visible, setPopUp);

  const isFullAccess =
    permissions[RBAC_TYPE_HOTEL_RESTAURANT] === RBAC_PERMISSION_LEVEL_FULL_ACCESS;

  const showFilter = isFullAccess && restaurantList.length > 1;

  return (
    <div className='mt-4 mb-4 group-list-container'>
      <SearchBar
        type={RESTAURANT_CHEF_CREATE}
        buttonName='Add Chef'
        setSearchText={setSearchText}
        onOpenDrawer={onOpenDrawer}
        displayAddOrEdit={isFullAccess ? 'show' : 'hide'}
        filterList={showFilter ? chefFilter : []}
        setFilterDataList={setChefFilter}
        filteredRecoard={filteredRecoard}
        visible={visible}
        setVisible={setVisible}
        popUp={popUp}
        setPopUp={setPopUp}
        placeHolder={chefList}
        setPlaceHolder={setChefList}
        globalPlaceHolder={allRestaurantChefList}
        setGlobalPlaceHolder={setAllRestaurantChef}
        dispatch={dispatch}
        mappingApi={createDataSortMapping}
        displayText='Restaurants chef'
        module='RESTAURANT_CHEF'
        searchText={tempSearchText}
        setSearch={setTempSearchText}
        actionMenuItems={[]}
        setDrag={setDragEnabled}
      />
      <div ref={wrapperRef} className='row mx-2 d-flex mt-4'>
        {allRestaurantLoader && (
          <div className='pt-4 col-md-12'>
            <LinearProgressBar />
          </div>
        )}
        <ChefListTable
          page={page}
          rowsPerPage={rowsPerPage}
          onOpenDrawer={onOpenDrawer}
          onViewDetails={onViewDetails}
          selectedValue={selectedValue}
          handleClose={handleClose}
          onClickView={onClickView}
          handleCellMenuClick={handleCellMenuClick}
          chefList={chefList}
          setChefList={setChefList}
          permissions={permissions}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          handleDeletePopupOpen={handleDeletePopupOpen}
          setVisible={setVisible}
          searchText={tempSearchText}
          dragDisabled={dragDisabled}
        />
      </div>
      {selectedValue.id && selectedValue.status === true && (
        <DeletePopupModel
          deletePopUp={selectedValue}
          onDelete={onDelete}
          onDeleteCancel={onDeleteCancel}
        />
      )}
    </div>
  );
};

export default ChefList;
